import {
  sessionFromView,
  type MySessionView,
} from '@obraplan/core/application/use-cases/access/get-my-session';
import type { Session } from '@obraplan/core/application/session';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import { createApiClient } from '@/infrastructure/http/api-client';
import {
  connectionStatus as statusDoAparelho,
  type ConnectionStatus,
} from '@/infrastructure/http/connection-status';
import {
  createOfflineFetch,
  DexieResponseStore,
  namespaceFor,
} from '@/infrastructure/http/offline-cache';
import { getDb } from '@/infrastructure/persistence/db';
import { DexieOutboxStore } from '@/infrastructure/persistence/outbox-store';
import { DexiePendingSignOuts } from '@/infrastructure/persistence/pending-sign-outs';
import {
  createOutbox,
  DEVICE_TIME_HEADER,
  OPERATION_HEADER,
} from '@/infrastructure/offline/outbox';
import { UuidV7Generator } from '@/infrastructure/id/uuid-v7-generator';
import { publishChange } from '@/lib/revalidation';
import type { ApiClient } from '@/infrastructure/http/api-client';
import { semResposta, type ConferenceUseCases } from './conference-operations';
import { createRemoteUseCases, type RemoteUseCases } from '@/infrastructure/http/remote-use-cases';
import {
  IMAGE_QUALITY,
  isImage,
  MAX_IMAGE_DIMENSION,
  type SupportedMimeType,
} from '@obraplan/core/domain/attachments/attachment';
import { CanvasImageCompressor } from '@/infrastructure/images/canvas-image-compressor';
import {
  createDeviceContainer,
  type Container,
  type ContainerOptions,
  type DeviceContainer,
} from './container';

/**
 * O container da aplicação: os dados moram no servidor.
 *
 * São duas metades. O aparelho (`createDeviceContainer` — o token da sessão, a obra aberta,
 * a cota) e, para todo o resto, a requisição ao servidor. Até a Fase 32 este container
 * nascia montando o local inteiro, sobre o IndexedDB, e trocava 201 das 227 chaves; agora
 * só nasce o que fica.
 */

/**
 * Onde o token vive enquanto a aba está aberta.
 *
 * Ele é gravado no aparelho (é o que evita pedir a senha toda manhã no tablet do
 * canteiro), mas essa leitura é assíncrona, e o cabeçalho de cada requisição precisa dele
 * **agora**. Daí a cópia em memória: a fonte continua sendo o aparelho, e esta é só a
 * mão que alcança.
 */
interface PorteDoToken {
  atual: string | undefined;
}

/** O que a tela alcança pela rede: tudo, menos o aparelho e o portão da sessão. */
type ChaveRemota = Exclude<
  keyof Container,
  keyof DeviceContainer | 'sessions' | 'connection' | 'outbox'
>;

export interface RemoteContainerOptions extends ContainerOptions {
  /** Onde a API responde. Vazio em produção: mesma origem da interface. */
  readonly baseUrl?: string;
  readonly fetch?: typeof globalThis.fetch;
  /** O estado da conexão que o indicador lê. O da aplicação é um só; o teste usa o dele. */
  readonly connectionStatus?: ConnectionStatus;
}

export function createRemoteContainer(options: RemoteContainerOptions = {}): Container {
  const local = createDeviceContainer(options);
  const porte: PorteDoToken = { atual: undefined };

  // A leitura guardada no aparelho (Fase 43, RN-597): o `fetch` que o cliente recebe entrega a
  // última resposta de sucesso quando falta sinal. O cliente e as telas não sabem da troca.
  const guardadas = new DexieResponseStore(options.db ?? getDb());
  const conexao = options.connectionStatus ?? statusDoAparelho;
  const client = createApiClient({
    ...(options.baseUrl === undefined ? {} : { baseUrl: options.baseUrl }),
    fetch: createOfflineFetch({
      fetch: options.fetch ?? globalThis.fetch.bind(globalThis),
      store: guardadas,
      clock: local.clock,
      status: conexao,
      isOnline: () => globalThis.navigator?.onLine !== false,
    }),
    token: () => porte.atual,
  });

  // Toda chave da tela que não é do aparelho precisa de endpoint no catálogo. Até a Fase 32 o
  // container local inteiro vinha por baixo e cobria a que faltasse; sem ele, a que faltar
  // seria `undefined` na primeira chamada. A anotação faz o compilador recusar: a chave sem
  // endpoint aparece aqui como propriedade ausente, e não na tela de alguém.
  const remotos: RemoteUseCases & Record<ChaveRemota, unknown> = createRemoteUseCases(client);

  // A fila da Conferência (Fase 43, etapa 2). Cada tentativa de uma operação ganha os seus
  // casos de uso: o mesmo transporte, com o id do passo e a hora do aparelho em cada pedido.
  const ids = new UuidV7Generator();
  const casosDaTentativa = (entrada: {
    readonly id: string;
    readonly createdAt: string;
  }): ConferenceUseCases => {
    let passo = 0;
    const comOPasso: ApiClient = {
      post: (path, body) => {
        const cabecalhos = {
          [OPERATION_HEADER]: `${entrada.id}#${String(passo)}`,
          [DEVICE_TIME_HEADER]: entrada.createdAt,
        };
        passo += 1;
        return client.post(path, body, { headers: cabecalhos });
      },
    };
    const daTentativa = createRemoteUseCases(comOPasso);
    // A lista é a de `ConferenceUseCases`, chave por chave: o `satisfies` recusa a que faltar.
    const casos = {
      openInspection: daTentativa.openInspection,
      adjustInspectionAllocation: daTentativa.adjustInspectionAllocation,
      recordPieceExecutions: daTentativa.recordPieceExecutions,
      recordDayMeasurement: daTentativa.recordDayMeasurement,
      confirmInspection: daTentativa.confirmInspection,
      registerInspectionAbsence: daTentativa.registerInspectionAbsence,
      reopenInspection: daTentativa.reopenInspection,
      attachInspectionFile: comprimindo(daTentativa.attachInspectionFile),
      attachNonConformityPhoto: comprimindo(daTentativa.attachNonConformityPhoto),
      recordTeamMeasurement: daTentativa.recordTeamMeasurement,
      attachTeamMeasurementPhoto: comprimindo(daTentativa.attachTeamMeasurementPhoto),
      setDailyReportWeather: daTentativa.setDailyReportWeather,
      setDailyReportTasks: daTentativa.setDailyReportTasks,
      setDailyReportNotes: daTentativa.setDailyReportNotes,
      setDailyReportCrew: daTentativa.setDailyReportCrew,
      setDailyReportEquipment: daTentativa.setDailyReportEquipment,
      getOrOpenDailyReport: daTentativa.getOrOpenDailyReport,
      attachDailyReportPhoto: comprimindo(daTentativa.attachDailyReportPhoto),
      setRoomNotApplicable: daTentativa.setRoomNotApplicable,
      recordRoomProgress: daTentativa.recordRoomProgress,
      // Leitura, e não gravação: o servidor não guarda o passo dela (ela só conta na numeração).
      getFrontPieces: daTentativa.getFrontPieces,
      recordReportedWork: daTentativa.recordReportedWork,
      recordReportedTeamWork: daTentativa.recordReportedTeamWork,
      removeInspectionAllocation: daTentativa.removeInspectionAllocation,
      removeInspectionAttachment: daTentativa.removeInspectionAttachment,
      resolveNonConformity: daTentativa.resolveNonConformity,
      closeDailyReport: daTentativa.closeDailyReport,
      reviseDailyReport: daTentativa.reviseDailyReport,
      // Leitura, como a das peças: a foto da equipe medida sem sinal acha a medição por ela.
      getDayTeamMeasurements: daTentativa.getDayTeamMeasurements,
    } satisfies Record<keyof ConferenceUseCases, unknown>;
    return casos as unknown as ConferenceUseCases;
  };
  const outbox = createOutbox({
    store: new DexieOutboxStore(options.db ?? getDb()),
    clock: local.clock,
    newId: () => ids.next(),
    useCasesFor: casosDaTentativa,
    // A placa de rede e a última resposta do servidor (RN-619): com a API sem responder há pouco
    // (Wi-Fi sem internet, 4G sem dados), a gravação vai direto para a fila, sem esperar.
    isOnline: () => globalThis.navigator?.onLine !== false && conexao.snapshot().online,
    onChange: publishChange,
  });

  /**
   * De um token para quem o portador é.
   *
   * No uso local isto era uma consulta ao próprio banco. Aqui é `GetMySession`, e a
   * diferença é a que a Fase 12 existe para criar: quem decide de quem é a sessão é o
   * servidor, e não o cliente lendo a própria tabela.
   *
   * O token vem por argumento e não do porte porque as duas coisas divergem exatamente uma
   * vez, e é a vez que importa: ao entrar, a sessão é resolvida com o segredo recém-obtido.
   */
  const resolverSessao = async (token: string): Promise<Result<Session, AppError>> => {
    porte.atual = token;
    const resposta = await client.post('/api/get-my-session', {});
    if (!resposta.ok) {
      // Token que não vale não pode ficar valendo em memória: a próxima requisição sairia
      // com ele de novo, e a tela veria uma recusa que já foi tratada.
      porte.atual = undefined;
      return err(resposta.error);
    }
    return ok(sessionFromView(resposta.value as MySessionView));
  };

  /*
   * ── Sair sem sinal (Fase 43, RN-616) ─────────────────────────────────────────
   *
   * Sem resposta do servidor, o token de quem saiu fica guardado, e a sessão dele é encerrada
   * quando o sinal voltar: ao abrir o sistema, a cada volta do sinal e, enquanto sobrar algum,
   * a cada 30 s. Cada encerramento vai com o token da sessão que saiu — o de quem entrou depois
   * no mesmo tablet não é tocado.
   */
  const saidas = new DexiePendingSignOuts(options.db ?? getDb());
  let enviandoSaidas = false;
  let proximaTentativa: ReturnType<typeof setTimeout> | undefined;
  const enviarSaidas = async (): Promise<void> => {
    if (enviandoSaidas) return;
    enviandoSaidas = true;
    let sobrou = false;
    try {
      for (const token of await saidas.list()) {
        const encerrada = await client.post('/api/sign-out', {}, { token });
        if (!encerrada.ok && semResposta(encerrada.error)) {
          sobrou = true;
          break;
        }
        // Encerrada, ou recusada porque já não valia (vencida, revogada): nada mais a fazer.
        await saidas.remove(token, local.clock.nowIso());
      }
    } catch {
      sobrou = true;
    } finally {
      enviandoSaidas = false;
    }
    if (sobrou && proximaTentativa === undefined) {
      proximaTentativa = setTimeout(() => {
        proximaTentativa = undefined;
        void enviarSaidas();
      }, 30_000);
    }
  };
  conexao.subscribe(() => {
    if (conexao.snapshot().online) void enviarSaidas();
  });

  return {
    ...local,
    // A única conversão de tipo da composição, e ela é inevitável: um objeto com
    // `execute` não tem como satisfazer *estruturalmente* uma classe com campo privado,
    // e a tela nunca usou nada além de `execute`. Forçar a compatibilidade produziria
    // acrobacia de tipos que não protege ninguém.
    //
    // Quem protege é o `endpoint-catalog.test.ts`: ele confere, contra o servidor de
    // verdade, que as chaves existem e que as formas de chamada batem. Isso um tipo não
    // teria como fazer, porque o que precisa concordar está do outro lado da rede.
    ...(remotos as unknown as Pick<Container, ChaveRemota>),

    /**
     * O `bootstrap` abre o banco do aparelho — o token e a obra aberta moram lá — e
     * ganha um passo: trazer o token do disco para a memória. Sem ele, a primeira
     * requisição depois de recarregar a página sairia anônima e a sessão pareceria ter
     * expirado sozinha.
     */
    bootstrap: async () => {
      await local.bootstrap();
      porte.atual = await local.getSessionToken.execute();
      // Quem saiu sem sinal da última vez: a sessão é encerrada agora, se houver sinal.
      void enviarSaidas();
    },

    /**
     * Sair. Sem resposta do servidor, a sessão não fica valendo: o token vai para a lista do
     * aparelho, e a sessão é encerrada quando o sinal voltar (RN-616). Para quem sai, deu certo.
     */
    signOut: {
      execute: async (input: unknown, session: Session) => {
        const saindo = porte.atual;
        const feito = (await remotos.signOut.execute(input, session)) as Result<void, AppError>;
        if (feito.ok || saindo === undefined || !semResposta(feito.error)) return feito;
        await saidas.add(saindo, local.clock.nowIso());
        return ok(undefined);
      },
    } as Container['signOut'],

    setSessionToken: {
      execute: async (token: string) => {
        porte.atual = token;
        return local.setSessionToken.execute(token);
      },
    } as Container['setSessionToken'],

    clearSessionToken: {
      execute: async () => {
        // O que a sessão leu sai com ela (RN-597): o próximo a entrar no tablet não o alcança,
        // e não há por que guardá-lo para um token que não vale mais.
        const saindo = porte.atual;
        porte.atual = undefined;
        if (saindo !== undefined) {
          await guardadas.forgetNamespace(await namespaceFor(saindo)).catch(() => undefined);
        }
        return local.clearSessionToken.execute();
      },
    } as Container['clearSessionToken'],

    sessions: { resolve: resolverSessao },
    connection: conexao,
    outbox,

    /*
     * ── A foto encolhe **antes** de subir ──────────────────────────────────────
     *
     * No uso local, quem comprime é o caso de uso, dentro do navegador, e os quatro
     * megabytes do celular nunca saem do aparelho. Com o caso de uso no servidor isso se
     * inverte: sem este passo, a foto crua atravessaria a rede do canteiro inteira para o
     * servidor guardar duzentos kilobytes — e o custo caro é justamente a subida.
     *
     * Por isso o compressor do servidor devolve `undefined`: o trabalho é feito aqui, e
     * lá o caso de uso guarda o que recebeu, que já chega pequeno. Falha na compressão
     * não impede o envio — foto grande é melhor do que foto perdida.
     */
    attachInspectionFile: comprimindo(
      remotos.attachInspectionFile,
    ) as Container['attachInspectionFile'],
    attachDailyReportPhoto: comprimindo(
      remotos.attachDailyReportPhoto,
    ) as Container['attachDailyReportPhoto'],
    attachNonConformityPhoto: comprimindo(
      remotos.attachNonConformityPhoto,
    ) as Container['attachNonConformityPhoto'],
    attachTeamMeasurementPhoto: comprimindo(
      remotos.attachTeamMeasurementPhoto,
    ) as Container['attachTeamMeasurementPhoto'],
  };
}

/** Entrada de anexo, no que este arquivo precisa dela. */
interface EntradaComBytes {
  readonly declaredMimeType: string;
  readonly bytes: Uint8Array;
}

/**
 * Envolve o envio remoto de anexo com a compressão local.
 *
 * O tamanho e a qualidade são os mesmos do caso de uso (`MAX_IMAGE_DIMENSION`,
 * `IMAGE_QUALITY`), e é de propósito: o que chega ao servidor tem de ser indistinguível do
 * que o uso local grava, ou a mesma foto teria dois tamanhos conforme onde o sistema roda.
 */
function comprimindo<T extends { execute: (...args: never[]) => unknown }>(remoto: T): T {
  const compressor = new CanvasImageCompressor();
  return {
    ...remoto,
    execute: async (entrada: EntradaComBytes, sessao: unknown) => {
      // O tipo declarado pelo usuário só é comprimível quando é imagem conhecida; o
      // `isImage` responde isso, e o compressor recusa o resto devolvendo `undefined`.
      const tipo = entrada.declaredMimeType as SupportedMimeType;
      const menor = isImage(tipo)
        ? await compressor.compress(entrada.bytes, tipo, {
            maxDimension: MAX_IMAGE_DIMENSION,
            quality: IMAGE_QUALITY,
          })
        : undefined;

      const enviar =
        menor === undefined
          ? entrada
          : { ...entrada, bytes: menor.bytes, declaredMimeType: menor.mimeType };

      return (remoto.execute as (e: unknown, s: unknown) => unknown)(enviar, sessao);
    },
  };
}
